import cloneDeep from "lodash.clonedeep";
import sortBy from "lodash.sortby";

import {
  CreateShippingRateGroupDto,
  CreateVariationDto,
  GenericVariationFe,
  ShippingRateGroupFe,
  VariationDto,
  VariationFe,
  VariationFeProps,
} from "@intelligems/ig-types";
import { genericSort } from "@src/utils/sortings";

import { initialFrontendProps } from "../../initials";

export const getVariationDtoFromFe = (
  variations: GenericVariationFe[]
): CreateVariationDto[] | VariationDto[] => {
  const isCustomTest = variations.some((group) =>
    group?.shippingRateGroups?.some(
      (rateGroup) => rateGroup.groupType === "custom"
    )
  );
  return variations.map((variation) => {
    return {
      id: variation.id,
      experienceId: variation.experienceId,
      name: variation.name,
      percentage: variation.percentage,
      isControl: variation.isControl,
      order: variation.order,
      priceChangeUnit: variation.priceChangeUnit,
      priceChange: variation.priceChange,
      priceRoundingType: variation.priceRoundingType,
      priceRoundingAmount: variation.priceRoundingAmount,
      shippingRateGroups: getShippingRateGroupsDtoFromFe(
        isCustomTest,
        variation || []
      ),
      excludeProducts: variation.excludeProducts,
      includeProducts: variation.includeProducts,
      testExperienceId: variation.testExperienceId,
      functionSyncError: variation.functionSyncError,
    };
  });
};

const getShippingRateGroupsDtoFromFe = (
  isCustomTest: boolean,
  group: GenericVariationFe
): CreateShippingRateGroupDto[] => {
  if (!isCustomTest) {
    return (group.shippingRateGroups || []).map((group) => ({
      ...group,
      rates: group.rates.map((rate) => ({
        ...rate,
        amount: rate.amount || 0,
        condition: rate.condition
          ? {
              id: rate.condition.id,
              min: rate.condition.min || 0,
              max: rate.condition.max,
              conditionType: rate.condition.conditionType,
              unit: rate.condition.unit,
            }
          : undefined,
      })),
    }));
  } else {
    const isGroupWithCustom = (group.shippingRateGroups || []).some(
      (rateGroup) => rateGroup.groupType === "custom"
    );
    if (!isGroupWithCustom) {
      return [];
    } else {
      return (group.shippingRateGroups || []).map((group) => ({
        ...group,
        rates: [],
      }));
    }
  }
};

/**
 * Every rate group except `custom` owns at least one rate — a `custom` test is
 * the only shape that persists an empty `rates` array on purpose. A non-custom
 * group with no rates is unrenderable (each rate type reads `rates[0]`) and
 * unsavable, so it is dropped here, at the DTO -> FE boundary, instead of being
 * guarded at every consumer. Positions are re-sequenced afterwards so the
 * remaining groups stay contiguous, matching `removeShippingRate`.
 */
export const ensureShippingRateGroups = (
  variation: VariationDto
): ShippingRateGroupFe[] => {
  return cloneDeep(variation.shippingRateGroups || [])
    .filter(
      (group) => group.groupType === "custom" || (group.rates || []).length > 0
    )
    .sort((a, b) => genericSort(a, b, "position"))
    .map((group, position) => ({ ...group, position }));
};
export const getVariationFeFromDto = (
  variations: VariationDto[]
): VariationFe[] => {
  return sortBy(
    variations.map((variation) => {
      const shippingRateGroups = ensureShippingRateGroups(variation);
      const frontendProps = ensureFrontendProps(variation);
      const ret: VariationFe = {
        ...variation,
        ...frontendProps,
        shippingRateGroups,
      };
      return ret;
    }),
    (_) => _.order
  );
};

export const ensureFrontendProps = (
  variation: VariationDto
): VariationFeProps => {
  return initialFrontendProps({
    name: variation.name,
  });
};
